import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import {
  Typography,
  Paper,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Checkbox,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ReceiptIcon from "@material-ui/icons/Receipt";
import moment from "moment";
import axios from "axios";
import { connect } from "redux-zero/react";
import actions from "../../actions";

const useStyles = makeStyles((theme) => ({
  topBar: {
    marginBottom: 10,
  },
  topBarControl: {
    marginLeft: 10,
  },
  amount: {
    width: 70,
  },
  whatsAppSent: {
    color: "green",
  },
  whatsAppNotSent: {
    color: "red",
  },
  whatsAppBillIcon: {
    color: "black",
  },
  tableContainer: {
    maxHeight: "70vh",
    overflow: "auto",
  },
  frozenHeaderCell: {
    position: "sticky",
    top: 0,
    zIndex: 3,
    background: theme.palette.background.paper,
  },
  frozenBodyCell: {
    position: "sticky",
    zIndex: 1,
    background: theme.palette.background.paper,
  },
  frozenBoundary: {
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  highlightHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  highlightFirstCell: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  highlightHeaderHover: {
    backgroundColor: theme.palette.grey[200],
  },
  highlightFirstCellHover: {
    backgroundColor: theme.palette.grey[200],
  },
}));

const FROZEN_COLUMN_WIDTHS = [50, 50, 50, 70, 70, 140, 150];

const FROZEN_COLUMN_OFFSETS = (() => {
  let acc = 0;
  return FROZEN_COLUMN_WIDTHS.map((width) => {
    const left = acc;
    acc += width;
    return left;
  });
})();

const mapToProps = ({ selectedSociety }) => ({ selectedSociety });

function BillsView({ selectedSociety }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [refreshData, setRefreshData] = React.useState(1);
  const [billsResponse, setBillsResponse] = useState({
    billItems: [],
    items: [],
  });
  const [changedBillNos] = useState(new Set());
  const [focusedRow, setFocusedRow] = useState(null);
  const [focusedCol, setFocusedCol] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredCol, setHoveredCol] = useState(null);
  const [whatsAppDialogOpen, setWhatsAppDialogOpen] = useState(false);
  const [whatsAppSending, setWhatsAppSending] = useState(false);
  const [whatsAppBillToggleRow, setWhatsAppBillToggleRow] = useState(null);
  const [whatsAppBillToggling, setWhatsAppBillToggling] = useState(false);
  const [testMobileNumber, setTestMobileNumber] = useState("");
  const isTestMobileNumberValid =
    testMobileNumber.trim() === "" || /^\d{10}$/.test(testMobileNumber.trim());
  let { billdate } = useParams();

  useEffect(
    () => {
      axios
        .get("/bill/bills/" + billdate, {
          headers: { "X-SocietyId": selectedSociety },
        })
        .then((response) => {
          var v = response.data;
          setBillsResponse((prev) => {
            let prevSelected = new Map(
              prev.items.map((item) => [item.billNo, item.selected]),
            );
            for (let item of v.items) {
              item.selected = prevSelected.has(item.billNo)
                ? prevSelected.get(item.billNo)
                : true;
            }
            return v;
          });
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSociety, refreshData],
  );

  function frozenColumnStyle(index) {
    let width = FROZEN_COLUMN_WIDTHS[index];
    return {
      left: FROZEN_COLUMN_OFFSETS[index],
      width,
      minWidth: width,
      maxWidth: width,
    };
  }

  function frozenColumnClass(index, rowIndex) {
    let isBoundary = index === FROZEN_COLUMN_WIDTHS.length - 1;
    let highlightClass = "";
    if (focusedRow === rowIndex) {
      highlightClass = classes.highlightFirstCell;
    } else if (hoveredRow === rowIndex || hoveredCol === index) {
      highlightClass = classes.highlightFirstCellHover;
    }
    return `${classes.frozenBodyCell} ${isBoundary ? classes.frozenBoundary : ""} ${highlightClass}`.trim();
  }

  function bodyColumnHoverClass(colIndex) {
    return hoveredCol === colIndex ? classes.highlightFirstCellHover : "";
  }

  function frozenHeaderColumnClass(index) {
    let isBoundary = index === FROZEN_COLUMN_WIDTHS.length - 1;
    let base = `${classes.frozenHeaderCell} ${isBoundary ? classes.frozenBoundary : ""}`.trim();
    return columnHighlightClass(index, base);
  }

  const FIXED_COLUMN_COUNT = FROZEN_COLUMN_WIDTHS.length;
  const billPrincipalColIndex =
    FIXED_COLUMN_COUNT + billsResponse.billItems.length;
  const billPrincipalNoInterestColIndex = billPrincipalColIndex + 1;
  const billInterestColIndex = billPrincipalNoInterestColIndex + 1;
  const billInterestOverrideColIndex = billInterestColIndex + 1;
  const billAmountColIndex = billInterestOverrideColIndex + 1;
  const arrearsPrincipalColIndex = billAmountColIndex + 1;
  const arrearsPrincipalNoInterestColIndex = arrearsPrincipalColIndex + 1;
  const arrearsInterestColIndex = arrearsPrincipalNoInterestColIndex + 1;
  const arrearsColIndex = arrearsInterestColIndex + 1;
  const grandTotalColIndex = arrearsColIndex + 1;

  function columnHighlightClass(colIndex, base = "") {
    if (focusedCol === colIndex) {
      return `${base} ${classes.highlightHeader}`.trim();
    }
    if (hoveredCol === colIndex) {
      return `${base} ${classes.highlightHeaderHover}`.trim();
    }
    return base;
  }

  function hoverHandlers(rowIndex, colIndex) {
    return {
      onMouseEnter: () => {
        setHoveredRow(rowIndex);
        setHoveredCol(colIndex);
      },
    };
  }

  function renderBillItemHeaders() {
    var ret = [];
    billsResponse.billItems.forEach((billItem, i) => {
      ret.push(
        <TableCell
          key={billItem.name}
          className={columnHighlightClass(FIXED_COLUMN_COUNT + i)}
        >
          {billItem.name}
        </TableCell>,
      );
    });
    return ret;
  }

  function renderBillItemAmountCells(row, rowIndex) {
    var ret = [];
    billsResponse.billItems.forEach((billItem, i) => {
      let colIndex = FIXED_COLUMN_COUNT + i;
      ret.push(
        <TableCell
          key={billItem.name}
          className={bodyColumnHoverClass(colIndex)}
          {...hoverHandlers(rowIndex, colIndex)}
        >
          <TextField
            type="number"
            className={classes.amount}
            id={"a" + billItem.itemIndex + "_" + row.billNo}
            inputProps={{ code: row.memberAccountCode }}
            defaultValue={row["a" + billItem.itemIndex]}
            onChange={() => changedBillNos.add(row.billNo)}
            onFocus={() => {
              setFocusedRow(rowIndex);
              setFocusedCol(colIndex);
            }}
            onBlur={() => {
              setFocusedRow(null);
              setFocusedCol(null);
            }}
            onWheel={(e) => e.target.blur()}
            onKeyUp={(e) => {
              if (e.keyCode === 13) {
                let nextRow = billsResponse.items[rowIndex + 1];
                if (nextRow) {
                  let nextElement = document.getElementById(
                    "a" + billItem.itemIndex + "_" + nextRow.billNo,
                  );
                  if (nextElement) {
                    nextElement.focus();
                    nextElement.select();
                  }
                }
              }
            }}
          />
        </TableCell>,
      );
    });
    return ret;
  }

  function save() {
    let changedRows = [];
    for (let billNo of changedBillNos) {
      let r = {
        billNo: billNo,
        a1: 0,
        a2: 0,
        a3: 0,
        a4: 0,
        a5: 0,
        a6: 0,
        a7: 0,
        a8: 0,
        a9: 0,
        a10: 0,
        a11: 0,
        a12: 0,
        a13: 0,
        a14: 0,
        a15: 0,
        a16: 0,
        a17: 0,
        a18: 0,
        a19: 0,
        a20: 0,
        billInterestOverride: 0,
      };

      for (let i = 1; i <= 20; i++) {
        let id = "a" + i + "_" + billNo;
        let t = document.getElementById(id);
        if (t) {
          if (!t.value) t.value = 0;
          r["a" + i] = parseFloat(t.value);
        } else {
          delete r["a" + i];
        }
      }
      let bio = document.getElementById("billInterestOverride_" + billNo);
      if (bio.value === null) {
        r.billInterestOverride = null;
      } else {
        r.billInterestOverride = parseFloat(bio.value);
      }

      changedRows.push(r);
    }

    if (changedRows.length > 0) {
      axios({
        method: "PUT",
        url: "/bill/bills",
        headers: { "X-SocietyId": selectedSociety },
        data: JSON.stringify(changedRows),
      })
        .then((response) => {
          changedBillNos.clear();
          setRefreshData(refreshData + 1);
          enqueueSnackbar("Saved", { variant: "success" });
        })
        .catch((error) => {
          alert(error.response.data.message);
        });
    } else {
      enqueueSnackbar("No changes", { variant: "danger" });
    }
  }

  function selectAll() {
    for (let item of billsResponse.items) {
      item.selected = true;
    }
    setBillsResponse(Object.assign({}, billsResponse));
  }

  function deselectAll() {
    for (let item of billsResponse.items) {
      item.selected = false;
    }
    setBillsResponse(Object.assign({}, billsResponse));
  }

  function selectWhatsApp() {
    for (let item of billsResponse.items) {
      item.selected = item.whatsAppBill;
    }
    setBillsResponse(Object.assign({}, billsResponse));
  }

  function selectPrinted() {
    for (let item of billsResponse.items) {
      item.selected = !item.whatsAppBill;
    }
    setBillsResponse(Object.assign({}, billsResponse));
  }

  function downloadRegisterExcel() {
    axios({
      method: "GET",
      url: "/bill/excel/bills/" + billdate,
      headers: { "X-SocietyId": selectedSociety },
      responseType: "arraybuffer",
    }).then((response) => {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "BillRegister.xlsx";
      link.click();
    });
  }

  function downloadRegisterPdf() {
    axios({
      method: "GET",
      url: "/bill/pdf/bills/" + billdate,
      headers: { "X-SocietyId": selectedSociety },
      responseType: "arraybuffer",
    }).then((response) => {
      const blob = new Blob([response.data], { type: "application/pdf" });
      window.open(window.URL.createObjectURL(blob));
    });
  }

  function downloadPdf() {
    let selected = [];
    for (let item of billsResponse.items) {
      if (item.selected) selected.push(item.billNo);
    }

    if (selected.length === 0) {
      alert("No bills selected");
      return;
    }

    axios({
      method: "POST",
      url: "/bill/pdf",
      headers: { "X-SocietyId": selectedSociety },
      data: JSON.stringify({
        billDate: billdate,
        billNos: Array.from(selected),
      }),
      responseType: "arraybuffer",
    }).then((response) => {
      const blob = new Blob([response.data], { type: "application/pdf" });
      window.open(window.URL.createObjectURL(blob));
    });
  }

  function openWhatsAppDialog() {
    let selected = [];
    for (let item of billsResponse.items) {
      if (item.selected) selected.push(item.billNo);
    }

    if (selected.length === 0) {
      alert("No bills selected");
      return;
    }

    setWhatsAppDialogOpen(true);
  }

  function closeWhatsAppDialog() {
    setWhatsAppDialogOpen(false);
    setTestMobileNumber("");
  }

  function sendByWhatsApp() {
    let selected = [];
    for (let item of billsResponse.items) {
      if (item.selected) selected.push(item.billNo);
    }

    let data = {
      billDate: billdate,
      billNos: Array.from(selected),
    };
    if (testMobileNumber.trim() !== "") {
      if (!isTestMobileNumberValid) return;
      data.testMobileNumber = testMobileNumber.trim();
    }

    setWhatsAppSending(true);
    axios({
      method: "POST",
      url: "/bill/sendbywhatsapp",
      headers: { "X-SocietyId": selectedSociety },
      data: JSON.stringify(data),
    })
      .then((response) => {
        enqueueSnackbar("Sent", { variant: "success" });
        closeWhatsAppDialog();
        setRefreshData(refreshData + 1);
      })
      .finally(() => {
        setWhatsAppSending(false);
      });
  }

  function openWhatsAppBillToggleDialog(row) {
    setWhatsAppBillToggleRow(row);
  }

  function closeWhatsAppBillToggleDialog() {
    setWhatsAppBillToggleRow(null);
  }

  function toggleWhatsAppBill() {
    let row = whatsAppBillToggleRow;

    setWhatsAppBillToggling(true);
    axios({
      method: "PUT",
      url: "/member/" + row.memberAccountCode + "/togglewhatsappbill",
      headers: { "X-SocietyId": selectedSociety },
    })
      .then((response) => {
        row.whatsAppBill = response.data.whatsAppBill;
        setBillsResponse(Object.assign({}, billsResponse));
        closeWhatsAppBillToggleDialog();
      })
      .finally(() => {
        setWhatsAppBillToggling(false);
      });
  }

  return (
    <>
      <Typography variant="h5">
        Bills for {moment(billdate).format("DD/MM/YYYY")}
      </Typography>
      <div className={classes.topBar}>
        {window.decodedJwt.role === "Administrator" && (
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={save}
            style={{ marginRight: 10 }}
          >
            Save
          </Button>
        )}

        <Button
          onClick={selectWhatsApp}
          variant="contained"
          size="small"
          color="primary"
          className={classes.topBarControl}
        >
          Select WhatsApp
        </Button>
        <Button
          onClick={openWhatsAppDialog}
          variant="contained"
          size="small"
          color="primary"
          className={classes.topBarControl}
          style={{ marginRight: 10 }}
        >
          Send WhatsApp
        </Button>
        <Button
          onClick={selectPrinted}
          variant="contained"
          size="small"
          color="primary"
          className={classes.topBarControl}
        >
          Select Printed
        </Button>
        <Button
          onClick={downloadPdf}
          variant="contained"
          size="small"
          color="primary"
          className={classes.topBarControl}
          style={{ marginRight: 10 }}
        >
          PDF
        </Button>
        <Button
          onClick={downloadRegisterExcel}
          variant="contained"
          size="small"
          color="primary"
          className={classes.topBarControl}
        >
          Register (Excel)
        </Button>
        <Button
          onClick={downloadRegisterPdf}
          variant="contained"
          size="small"
          color="primary"
          className={classes.topBarControl}
          style={{ marginRight: 10 }}
        >
          Register (PDF)
        </Button>
        <Button
          onClick={deselectAll}
          variant="contained"
          size="small"
          color="primary"
          className={classes.topBarControl}
        >
          Deselect All
        </Button>
        <Button
          onClick={selectAll}
          variant="contained"
          size="small"
          color="primary"
          className={classes.topBarControl}
        >
          Select All
        </Button>
      </div>
      <form
        autoComplete="off"
        onKeyDown={(e) => {
          if (e.keyCode === 13) e.preventDefault();
        }}
      >
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  className={frozenHeaderColumnClass(0)}
                  style={frozenColumnStyle(0)}
                ></TableCell>
                <TableCell
                  title="WhatsApp Bill"
                  className={frozenHeaderColumnClass(1)}
                  style={frozenColumnStyle(1)}
                >
                  Type
                </TableCell>
                <TableCell
                  title="WhatsApp Sent Status"
                  className={frozenHeaderColumnClass(2)}
                  style={frozenColumnStyle(2)}
                >
                  Sent
                </TableCell>
                <TableCell
                  className={frozenHeaderColumnClass(3)}
                  style={frozenColumnStyle(3)}
                >
                  Bill No
                </TableCell>
                <TableCell
                  className={frozenHeaderColumnClass(4)}
                  style={frozenColumnStyle(4)}
                >
                  Code
                </TableCell>
                <TableCell
                  className={frozenHeaderColumnClass(5)}
                  style={frozenColumnStyle(5)}
                >
                  Flat
                </TableCell>
                <TableCell
                  className={frozenHeaderColumnClass(6)}
                  style={frozenColumnStyle(6)}
                >
                  Name
                </TableCell>
                {renderBillItemHeaders()}
                <TableCell className={columnHighlightClass(billPrincipalColIndex)}>
                  Bill Principal
                </TableCell>
                <TableCell
                  className={columnHighlightClass(
                    billPrincipalNoInterestColIndex,
                  )}
                >
                  Bill Principal (Interest N/A)
                </TableCell>
                <TableCell className={columnHighlightClass(billInterestColIndex)}>
                  Bill Interest
                </TableCell>
                <TableCell
                  className={columnHighlightClass(
                    billInterestOverrideColIndex,
                  )}
                >
                  Bill Interest Override
                </TableCell>
                <TableCell className={columnHighlightClass(billAmountColIndex)}>
                  Bill Amount
                </TableCell>
                <TableCell
                  className={columnHighlightClass(arrearsPrincipalColIndex)}
                >
                  Arrears Principal
                </TableCell>
                <TableCell
                  className={columnHighlightClass(
                    arrearsPrincipalNoInterestColIndex,
                  )}
                >
                  Arrears Principal (Interest N/A)
                </TableCell>
                <TableCell className={columnHighlightClass(arrearsInterestColIndex)}>
                  Arrears Interest
                </TableCell>
                <TableCell className={columnHighlightClass(arrearsColIndex)}>
                  Arrears
                </TableCell>
                <TableCell className={columnHighlightClass(grandTotalColIndex)}>
                  Grand Total
                </TableCell>
              </TableRow>
            </TableHead>
            {
              <TableBody
                onMouseLeave={() => {
                  setHoveredRow(null);
                  setHoveredCol(null);
                }}
              >
                {billsResponse.items.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    className={
                      hoveredRow === rowIndex
                        ? classes.highlightFirstCellHover
                        : ""
                    }
                  >
                    <TableCell
                      className={frozenColumnClass(0, rowIndex)}
                      style={frozenColumnStyle(0)}
                      {...hoverHandlers(rowIndex, 0)}
                    >
                      <Checkbox
                        checked={row.selected}
                        onChange={(e) => {
                          row.selected = e.target.checked;
                          setBillsResponse(Object.assign({}, billsResponse));
                        }}
                      />
                    </TableCell>
                    <TableCell
                      className={frozenColumnClass(1, rowIndex)}
                      style={frozenColumnStyle(1)}
                      {...hoverHandlers(rowIndex, 1)}
                    >
                      <IconButton
                        size="small"
                        onClick={() => openWhatsAppBillToggleDialog(row)}
                      >
                        {row.whatsAppBill ? (
                          <Tooltip title="WhatsApp Bill">
                            <WhatsAppIcon
                              className={classes.whatsAppBillIcon}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Regular Bill">
                            <ReceiptIcon className={classes.whatsAppBillIcon} />
                          </Tooltip>
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell
                      className={frozenColumnClass(2, rowIndex)}
                      style={frozenColumnStyle(2)}
                      {...hoverHandlers(rowIndex, 2)}
                    >
                      {row.whatsAppSent ? (
                        <CheckCircleIcon className={classes.whatsAppSent} />
                      ) : (
                        row.whatsAppResponse != null && (
                          <Tooltip title={row.whatsAppResponse}>
                            <CancelIcon className={classes.whatsAppNotSent} />
                          </Tooltip>
                        )
                      )}
                    </TableCell>
                    <TableCell
                      className={frozenColumnClass(3, rowIndex)}
                      style={frozenColumnStyle(3)}
                      {...hoverHandlers(rowIndex, 3)}
                    >
                      {row.billNo}
                    </TableCell>
                    <TableCell
                      className={frozenColumnClass(4, rowIndex)}
                      style={frozenColumnStyle(4)}
                      {...hoverHandlers(rowIndex, 4)}
                    >
                      {row.memberAccountCode}
                    </TableCell>
                    <TableCell
                      className={frozenColumnClass(5, rowIndex)}
                      style={frozenColumnStyle(5)}
                      {...hoverHandlers(rowIndex, 5)}
                    >
                      {[row.buildingNo, row.wing, row.unit]
                        .filter(Boolean)
                        .join("-")}
                    </TableCell>
                    <TableCell
                      className={frozenColumnClass(6, rowIndex)}
                      style={frozenColumnStyle(6)}
                      {...hoverHandlers(rowIndex, 6)}
                    >
                      {row.name}
                    </TableCell>
                    {renderBillItemAmountCells(row, rowIndex)}
                    <TableCell
                      className={bodyColumnHoverClass(billPrincipalColIndex)}
                      {...hoverHandlers(rowIndex, billPrincipalColIndex)}
                    >
                      {row.billPrincipal}
                    </TableCell>
                    <TableCell
                      className={bodyColumnHoverClass(
                        billPrincipalNoInterestColIndex,
                      )}
                      {...hoverHandlers(
                        rowIndex,
                        billPrincipalNoInterestColIndex,
                      )}
                    >
                      {row.billPrincipalNoInterest}
                    </TableCell>
                    <TableCell
                      title={row.billInterestDetails}
                      className={bodyColumnHoverClass(billInterestColIndex)}
                      {...hoverHandlers(rowIndex, billInterestColIndex)}
                    >
                      {row.billInterest}
                    </TableCell>
                    <TableCell
                      className={bodyColumnHoverClass(
                        billInterestOverrideColIndex,
                      )}
                      {...hoverHandlers(rowIndex, billInterestOverrideColIndex)}
                    >
                      <TextField
                        type="number"
                        className={classes.amount}
                        id={"billInterestOverride_" + row.billNo}
                        inputProps={{ code: row.memberAccountCode }}
                        defaultValue={row.billInterestOverride}
                        onChange={() => changedBillNos.add(row.billNo)}
                        onFocus={() => {
                          setFocusedRow(rowIndex);
                          setFocusedCol(billInterestOverrideColIndex);
                        }}
                        onBlur={() => {
                          setFocusedRow(null);
                          setFocusedCol(null);
                        }}
                        onWheel={(e) => e.target.blur()}
                        onKeyUp={(e) => {
                          if (e.keyCode === 13) {
                            let nextRow = billsResponse.items[rowIndex + 1];
                            if (nextRow) {
                              let nextElement = document.getElementById(
                                "billInterestOverride_" + nextRow.billNo,
                              );
                              if (nextElement) {
                                nextElement.focus();
                                nextElement.select();
                              }
                            }
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell
                      className={bodyColumnHoverClass(billAmountColIndex)}
                      {...hoverHandlers(rowIndex, billAmountColIndex)}
                    >
                      {row.billAmount}
                    </TableCell>
                    <TableCell
                      className={bodyColumnHoverClass(arrearsPrincipalColIndex)}
                      {...hoverHandlers(rowIndex, arrearsPrincipalColIndex)}
                    >
                      {row.arrearsPrincipal}
                    </TableCell>
                    <TableCell
                      className={bodyColumnHoverClass(
                        arrearsPrincipalNoInterestColIndex,
                      )}
                      {...hoverHandlers(
                        rowIndex,
                        arrearsPrincipalNoInterestColIndex,
                      )}
                    >
                      {row.arrearsPrincipalNoInterest}
                    </TableCell>
                    <TableCell
                      className={bodyColumnHoverClass(arrearsInterestColIndex)}
                      {...hoverHandlers(rowIndex, arrearsInterestColIndex)}
                    >
                      {row.arrearsInterest}
                    </TableCell>
                    <TableCell
                      className={bodyColumnHoverClass(arrearsColIndex)}
                      {...hoverHandlers(rowIndex, arrearsColIndex)}
                    >
                      {row.arrears}
                    </TableCell>
                    <TableCell
                      className={bodyColumnHoverClass(grandTotalColIndex)}
                      {...hoverHandlers(rowIndex, grandTotalColIndex)}
                    >
                      {row.grandTotal}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            }
          </Table>
        </TableContainer>
      </form>
      <Dialog
        open={whatsAppDialogOpen}
        onClose={closeWhatsAppDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Send WhatsApp</DialogTitle>
        <DialogContent>
          Send WhatsApp bill notifications for the selected bills?
          <br />
          <br />
          <TextField
            label="Test Mobile Number"
            helperText={
              isTestMobileNumberValid
                ? "If set, all messages are sent to this number instead of the members'"
                : "Must be blank or a 10 digit mobile number"
            }
            error={!isTestMobileNumberValid}
            fullWidth
            value={testMobileNumber}
            onChange={(e) => setTestMobileNumber(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeWhatsAppDialog} disabled={whatsAppSending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isTestMobileNumberValid || whatsAppSending}
            onClick={sendByWhatsApp}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={whatsAppBillToggleRow != null}
        onClose={closeWhatsAppBillToggleDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Change Bill Type</DialogTitle>
        <DialogContent>
          {whatsAppBillToggleRow != null &&
            (whatsAppBillToggleRow.whatsAppBill
              ? "Switch " + whatsAppBillToggleRow.name + " to a regular bill?"
              : "Switch " +
                whatsAppBillToggleRow.name +
                " to a WhatsApp bill?")}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeWhatsAppBillToggleDialog}
            disabled={whatsAppBillToggling}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={whatsAppBillToggling}
            onClick={toggleWhatsAppBill}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default connect(mapToProps, actions)(BillsView);
